//@ts-nocheck
import { Col, Form, Input, Row, Spin, Typography } from 'antd';
import React from 'react';
import { Link, useLocation } from 'react-router';
import ReCAPTCHA from 'react-google-recaptcha';
import { ScopeButton } from './buttons';
import { FormInput, FormPasswordInput } from './formInputs';
import { FormWrapper } from './formWrappers';
import { ThemedLogo } from './ThemedLogo';
import { BoldSpanLargeSize, BoldSpanMidSize } from './formSpans';
import { AuthA11yToggle } from './AuthA11yToggle';
import { useAuthA11yMode } from '../hooks/useAuthA11yMode';
import {
  SsoProviderList,
  authSsoProviders,
  type SsoProviderId,
} from './SsoProviderButton';

export type AuthProviderId = SsoProviderId;

export interface LoginFormMfaResponse {
  useMFA: boolean;
  email: string;
  password: string;
}

export interface LoginFormLabels {
  welcome: string;
  siteTitle: string;
  email: string;
  password: string;
  forgotPassword: string;
  login: string;
  or: string;
  xeroLogin: string;
  googleLogin: string;
  microsoftLogin: string;
  qboLogin: string;
  zohoLogin: string;
  noAccount: string;
  signup: string;
  mfaPrompt: string;
  mfaCodeLabel: string;
  requiredField: string;
  invalidEmail: string;
  confirm: string;
  backToLogin: string;
  logoAriaLabel: string;
  accessibilityMode: string;
  accessibilityModeHelp: string;
}

export interface LoginFormAssets {
  logo: string;
  logoDark?: string;
  xero: string;
  google: string;
  microsoft: string;
  qbo: string;
  qboHover?: string;
  zoho: string;
}

export interface LoginFormRoutes {
  login: string;
  main: string;
  signUp: string;
  signUpRoot?: string;
  resetPassword: string;
  resetPasswordRoot?: string;
}

export interface LoginFormProps {
  onLogin: (
    email: string,
    password: string,
    gCaptchaResponse: string,
    mfaCode: string | null,
  ) => void;
  onProviderLogin: (provider: AuthProviderId) => void;
  loading?: boolean;
  mfaResponse?: LoginFormMfaResponse;
  labels: LoginFormLabels;
  assets: LoginFormAssets;
  routes: LoginFormRoutes;
  captchaSiteKey: string;
  showZoho?: boolean;
  /** When false, accessibility mode switch is not rendered. Defaults to true. */
  showA11yToggle?: boolean;
  onA11yDisabled?: () => void;
  /** Logo display width (e.g. "280px"). Defaults to CenterLogo default. */
  logoWidth?: string;
  /** Space between logo and title row. Defaults to 20px. */
  titlePaddingTop?: string | number;
}

interface LoginFormValues {
  email: string;
  password: string;
}

interface LoginFormValuesMfa {
  email: string;
  password: string;
  mfaCode: string;
}

/** Default English labels matching generate.TAX en_US login copy. */
export const DEFAULT_LOGIN_FORM_LABELS: LoginFormLabels = {
  welcome: 'Welcome to',
  siteTitle: 'generate.TAX',
  email: 'Email address',
  password: 'Password',
  forgotPassword: 'Forgot Password?',
  login: 'Login',
  or: 'OR',
  xeroLogin: 'Continue with Xero',
  googleLogin: 'Continue with Google',
  microsoftLogin: 'Continue with Microsoft',
  qboLogin: 'Continue with Intuit',
  zohoLogin: 'Continue with Zoho',
  noAccount: "Don't have an account?",
  signup: 'Register with Email',
  mfaPrompt: 'Enter your MFA code to continue',
  mfaCodeLabel: 'MFA Code',
  requiredField: 'This field is required',
  invalidEmail: 'Please enter a valid email',
  confirm: 'Confirm',
  backToLogin: 'Back to login',
  logoAriaLabel: 'generate.TAX logo',
  accessibilityMode: 'Accessibility mode',
  accessibilityModeHelp:
    'Shows field labels, higher contrast, and clearer focus and links',
};

export const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  onProviderLogin,
  loading,
  mfaResponse,
  labels,
  assets,
  routes,
  captchaSiteKey,
  showZoho = true,
  showA11yToggle = true,
  onA11yDisabled,
  logoWidth,
  titlePaddingTop = 12,
}) => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { a11yMode, handleA11yModeChange, authFormClassName } =
    useAuthA11yMode(onA11yDisabled);
  const wasLoadingRef = React.useRef(false);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  React.useEffect(() => {
    if (loading === false) {
      setIsSubmitting(false);
      if (wasLoadingRef.current && mfaResponse?.useMFA) {
        form.setFieldsValue({ mfaCode: '' });
      }
    }
    wasLoadingRef.current = !!loading;
  }, [loading, mfaResponse?.useMFA, form]);

  const captchaEnabled = Boolean(captchaSiteKey);

  const resolveCaptchaToken = async (): Promise<string> => {
    if (!captchaEnabled) return '';
    return (await recaptchaRef.current?.executeAsync()) || '';
  };

  const handleSubmit = async (values: LoginFormValues): Promise<void> => {
    const gCaptchaResponse = await resolveCaptchaToken();
    onLogin(values.email, values.password, gCaptchaResponse, null);
  };

  const handleSubmitMfa = async (values: LoginFormValuesMfa): Promise<void> => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const gCaptchaResponse = await resolveCaptchaToken();
    onLogin(values.email, values.password, gCaptchaResponse, values.mfaCode);
  };

  const logoLinkTo = !location.pathname.includes(routes.login)
    ? !location.pathname.includes(routes.signUpRoot || routes.signUp) &&
      !location.pathname.includes(
        routes.resetPasswordRoot || routes.resetPassword,
      )
      ? routes.main
      : routes.login
    : '#';

  return (
    <Spin spinning={loading} size="large">
      <FormWrapper>
        <Link to={logoLinkTo} aria-label={labels.logoAriaLabel}>
          <ThemedLogo
            src={assets.logo}
            srcDark={assets.logoDark}
            alt=""
            width={logoWidth}
          />
        </Link>
        <Row justify="center" style={{ paddingTop: titlePaddingTop }}>
          <Col span={24}>
            {!mfaResponse?.useMFA && (
              <Typography.Title
                level={2}
                style={{
                  fontSize: '24px',
                  fontWeight: 400,
                  lineHeight: '24px',
                  textAlign: 'center',
                  color: 'var(--color-text-base, #2e2e2e)',
                  marginTop: 0,
                  marginBottom: 0,
                }}
              >
                {labels.welcome}
                <BoldSpanLargeSize> {labels.siteTitle}</BoldSpanLargeSize>
              </Typography.Title>
            )}
            {mfaResponse?.useMFA ? (
              <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                className={`${authFormClassName} scope-login-mfa`}
                onFinish={handleSubmitMfa}
                style={{ marginBottom: '30px' }}
              >
                <Typography.Title
                  level={4}
                  className="scope-login-mfa__title"
                  style={{
                    fontSize: '24px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    textAlign: 'center',
                    color: 'var(--color-text-dark, #042a2a)',
                    marginTop: '70px',
                  }}
                >
                  {labels.mfaPrompt}
                </Typography.Title>

                <Form.Item
                  name="email"
                  hidden
                  initialValue={mfaResponse?.email}
                >
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item
                  name="password"
                  hidden
                  initialValue={mfaResponse?.password}
                >
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item
                  className="scope-login-mfa__field"
                  name="mfaCode"
                  label={labels.mfaCodeLabel}
                  rules={[{ required: true, message: labels.requiredField }]}
                >
                  <Input
                    id="mfa-code"
                    maxLength={6}
                    size="large"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        form.submit();
                      } else if (
                        !/[0-9]/.test(event.key) &&
                        event.key !== 'Backspace' &&
                        event.key !== 'Delete' &&
                        event.key !== 'Tab' &&
                        event.key !== 'ArrowLeft' &&
                        event.key !== 'ArrowRight' &&
                        !(event.ctrlKey && event.key === 'v') &&
                        !(event.metaKey && event.key === 'v')
                      ) {
                        event.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item className="scope-login-mfa__cta">
                  <ScopeButton
                    type="primary"
                    htmlType="submit"
                    disabled={isSubmitting || loading}
                  >
                    {labels.confirm}
                  </ScopeButton>
                </Form.Item>
                <Link
                  className="link link-text scope-login-mfa__back"
                  to={routes.login}
                >
                  {labels.backToLogin}
                </Link>
                {captchaEnabled ? (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey={captchaSiteKey}
                  />
                ) : null}
              </Form>
            ) : (
              <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                className={authFormClassName}
                onFinish={handleSubmit}
                style={{ marginBottom: '30px' }}
              >
                <div className="scope-login-fields">
                  <Form.Item
                    name="email"
                    label={labels.email}
                    rules={[
                      { required: true, message: labels.requiredField },
                      { type: 'email', message: labels.invalidEmail },
                    ]}
                  >
                    <FormInput
                      width="100%"
                      placeholder={labels.email}
                      size="large"
                      autoComplete="username"
                    />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    label={labels.password}
                    rules={[{ required: true, message: labels.requiredField }]}
                  >
                    <FormPasswordInput
                      placeholder={labels.password}
                      size="large"
                      autoComplete="current-password"
                    />
                  </Form.Item>
                  <Link
                    className="link link-text password-link"
                    to={routes.resetPassword}
                  >
                    {labels.forgotPassword}
                  </Link>
                  <Form.Item
                    className="scope-auth-primary-cta"
                    style={{ marginBottom: 0 }}
                  >
                    <ScopeButton type="primary" htmlType="submit" width="100%">
                      {labels.login}
                    </ScopeButton>
                  </Form.Item>
                </div>
                <Row
                  className="scope-login-or"
                  justify="center"
                  align="middle"
                  style={{ height: '50px' }}
                >
                  <Col
                    span={4}
                    style={{
                      backgroundColor: 'var(--color-border, #d5dce0)',
                      height: '1px',
                    }}
                  />
                  <Col
                    span={2}
                    style={{
                      textAlign: 'center',
                      color: 'var(--color-text-dark, #042a2a)',
                      fontSize: '18px',
                      lineHeight: '24px',
                      fontWeight: 600,
                    }}
                  >
                    {labels.or}
                  </Col>
                  <Col
                    span={4}
                    style={{
                      backgroundColor: 'var(--color-border, #d5dce0)',
                      height: '1px',
                    }}
                  />
                </Row>
                {captchaEnabled ? (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey={captchaSiteKey}
                  />
                ) : null}
                <SsoProviderList
                  layout="stack"
                  showZoho={showZoho}
                  items={authSsoProviders({
                    labels,
                    assets,
                    onProviderLogin,
                  })}
                />
                <Row style={{ marginTop: '20px' }}>
                  <Col span={24} style={{ textAlign: 'center' }}>
                    <BoldSpanMidSize>{labels.noAccount} </BoldSpanMidSize>
                    <Link className="sign-up-link" to={routes.signUp}>
                      {labels.signup}
                    </Link>
                  </Col>
                </Row>
              </Form>
            )}
          </Col>
        </Row>
        {showA11yToggle ? (
          <AuthA11yToggle
            placement="footer"
            checked={a11yMode}
            onChange={handleA11yModeChange}
            label={labels.accessibilityMode}
            helpText={labels.accessibilityModeHelp}
          />
        ) : null}
      </FormWrapper>
    </Spin>
  );
};
